import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  MenuItem,
  Select,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import MapComponent from "../components/map/MapComponent";
import { compareDate, getApiKey } from "../helpers/Utils";
import { useNavigate } from "react-router-dom";
import { callApi } from "../helpers/CallAPI";
import { apiBaseUrl, apiPaths } from "../constants/ApiUrl";
import NavBar from "../components/navbar/NavBar";
import NewsItemComponent from "../components/news-item/NewsItemComponent";
import NewsDetailComponent from "../components/news-item/NewsDetailComponent";
import { filters, newsTypes } from "../constants/Dashboard";
import "../styles/Dashboard.css";
import "../styles/Tooltip.css";
import "../styles/CustomSelect.css";
import TickPlacementBars from "../components/chart/BarChart";

const Dashboard: React.FC = () => {
  const [dashboardList, setDashboardList] = useState([]);
  const [displayTypes, setDisplayTypes] = useState([]);
  const [newsData, setNewsData] = useState(null);
  const [serveData, setServeData] = useState<any>(null);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedDisplayType, setSelectedDisplayType] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    getData();
  }, []);
  const getData = () => {
    const apiKey = getApiKey();
    if (apiKey) {
      callApi(apiKey, apiBaseUrl + apiPaths.versions, "GET")
        .then((data) => {
          setDashboardList(data);
        })
        .catch((e) => {
          const error = JSON.parse(e.message);
          if (error.status === 403) {
            navigate("/login");
          }
        });
      callApi(apiKey, apiBaseUrl + apiPaths.displayType, "GET")
        .then((data) => {
          setDisplayTypes(data);
        })
        .catch((e) => {
          const error = JSON.parse(e.message);
          if (error.status === 403) {
            navigate("/login");
          }
        });
    } else {
      navigate("/login");
    }
  };
  const getDataInDashboard = async (did) => {
    const apiKey = getApiKey();
    if (apiKey) {
      try {
        const data = await callApi(
          apiKey,
          apiBaseUrl + apiPaths.results + "?did=" + did,
          "GET"
        );
        return {
          data: data,
        };
      } catch (e) {
        const error = JSON.parse(e.message);
        if (error.status === 403) {
          navigate("/login");
        }
      }
    } else {
      navigate("/login");
    }
  };

  const handleGetNewsData = (did) => {
    getDataInDashboard(did).then((result) => {
      const data = [];
      const updatedResultWithNewsType = result.data?.map((obj) => ({
        ...obj,
        newsTypeIcon: newsTypes.find((x) => x.name === obj.dataSource).icon,
      }));
      data.push(...updatedResultWithNewsType);
      const sortData = data.sort((a, b) => {
        return compareDate(a.time, b.time);
      });
      setNewsData(sortData);
      filterData(sortData, selectedFilter);
    });
  };
  const setDefaultDashboardDisplayType = (did) => {
    const dashboard = dashboardList.find((x) => x.did === did);
    const displayType = displayTypes.find(
      (x) => x.id === dashboard.displayType
    );
    setSelectedDisplayType(displayType);
  };
  const handleSelectDashboard = (event) => {
    const did = event.target.value;
    setSelectedDashboard(did);
    handleGetNewsData(did);
    setDefaultDashboardDisplayType(did);
  };

  const handleClearSelectDashboard = () => {
    setSelectedDashboard(null);
    setNewsData(null);
    filterData(null, selectedFilter);
  };
  const filterData = (data, value) => {
    const filterData = value
      ? data?.filter(
          (item) =>
            new Date(item.time) >= filters.find((x) => x.value === value).func
        )
      : data;
    setServeData(filterData);
  };
  const handleSelectFilter = (event) => {
    const value = event.target.value;
    setSelectedFilter(value);
    filterData(newsData, value);
  };
  const handleSelectDisplayType = (event) => {
    const value = event.target.value;
    const displayType = displayTypes.find((x) => x.id === value);
    setSelectedDisplayType(displayType);
  };
  const handleClearSelectFilter = (event) => {
    setSelectedFilter(null);
    filterData(newsData, null);
  };
  const handleListItemClick = (news) => {
    setSelectedNews(news);
  };

  const handleClose = () => {
    setSelectedNews(null);
  };

  const handleDiscardResult = (resultId) => {
    const apiKey = getApiKey();
    if (apiKey) {
      callApi(
        apiKey,
        apiBaseUrl +
          apiPaths.results +
          "/" +
          selectedDashboard +
          "/" +
          resultId,
        "POST"
      )
        .then((data) => {
          handleGetNewsData(selectedDashboard);
        })
        .catch((e) => {
          console.log(e);
        });
    } else {
      navigate("/login");
    }
  };

  return (
    <>
      <Box className="container">
        <NavBar />
        <Box className="select-dashboard-bar">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Select
                displayEmpty
                renderValue={(value: any) => {
                  if (!value) {
                    return (
                      <Typography color="gray">Select dashboard</Typography>
                    );
                  }
                  return dashboardList?.find((x) => x.did === value).name;
                }}
                value={selectedDashboard}
                className="custom-select"
                onChange={(e) => handleSelectDashboard(e)}
              >
                {dashboardList?.map((dashboard, index) => {
                  return (
                    <MenuItem key={index} value={dashboard.did}>
                      {dashboard.name}
                    </MenuItem>
                  );
                })}
              </Select>

              {selectedDashboard ? (
                <IconButton onClick={(e) => handleClearSelectDashboard()}>
                  <FilterAltOffIcon sx={{ color: "#ffffff" }} />
                </IconButton>
              ) : null}
            </Box>
          </Box>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ display: "flex", marginRight: "20px" }}>
              <Select
                displayEmpty
                renderValue={(value: any) => {
                  if (!value) {
                    return <Typography color="gray">Display type</Typography>;
                  }
                  return displayTypes?.find((x) => x.id === value).type;
                }}
                value={selectedDisplayType ? selectedDisplayType.id : null}
                className="custom-select"
                onChange={(e) => handleSelectDisplayType(e)}
              >
                {displayTypes?.map((displayType, index) => {
                  return (
                    <MenuItem key={index} value={displayType.id}>
                      {displayType.type}
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
            <Box sx={{ display: "flex" }}>
              <Select
                displayEmpty
                renderValue={(value: any) => {
                  if (!value) {
                    return <Typography color="gray">Filter data</Typography>;
                  }
                  return filters.find((x) => x.value === value).name;
                }}
                value={selectedFilter}
                className="custom-select"
                onChange={(e) => handleSelectFilter(e)}
              >
                {filters.map((filter, index) => {
                  return (
                    <MenuItem key={index} value={filter.value}>
                      {filter.name}
                    </MenuItem>
                  );
                })}
              </Select>
              {selectedFilter ? (
                <IconButton onClick={(e) => handleClearSelectFilter(e)}>
                  <FilterAltOffIcon sx={{ color: "#ffffff" }} />
                </IconButton>
              ) : null}
            </Box>
          </Box>
        </Box>
        <Box className="main-content">
          <Box sx={{ display: "flex", flex: 1 }}>
            <Box className="left-section">
              <Box className="left-section-inner">
                {serveData?.length === 0 && (
                  <Box className="no-result">
                    <Typography sx={{ color: "#818589" }}>
                      No results
                    </Typography>
                  </Box>
                )}
                <List>
                  {serveData ? (
                    <>
                      {serveData.map((news, index) => (
                        <>
                          <ListItem className="list-item" key={index}>
                            <NewsItemComponent
                              index={index}
                              news={news}
                              onDelete={handleDiscardResult}
                              onClick={handleListItemClick}
                            />
                          </ListItem>
                          {index < serveData.length - 1 && (
                            <Divider
                              style={{
                                background: "#ffffff",
                                width: "95%",
                                marginTop: "8px",
                                paddingLeft: "20px",
                              }}
                            />
                          )}
                        </>
                      ))}
                    </>
                  ) : null}
                </List>
              </Box>
            </Box>
            <Tooltip
              open={selectedNews ? true : false} // Open the Tooltip when clicked
              slotProps={{
                popper: {
                  disablePortal: true,
                },
              }}
              title={
                <>
                  {selectedNews && (
                    <Box className="tooltip-box">
                      <NewsDetailComponent news={selectedNews} />
                      <Button
                        onClick={handleClose}
                        variant="contained"
                        color="primary"
                        sx={{ marginTop: 2 }}
                      >
                        Close
                      </Button>
                    </Box>
                  )}
                </>
              }
              placement="right"
            >
              <span />
            </Tooltip>
            {selectedDisplayType && selectedDisplayType.type === "Map" ? (
              <Box sx={{ flex: 1, height: "100%" }}>
                <MapComponent
                  newsData={serveData}
                  selectedNews={selectedNews}
                />
              </Box>
            ) : null}
            {selectedDisplayType && selectedDisplayType.type === "Chart" ? (
              <TickPlacementBars
                newsData={serveData}
                selectedFilter={selectedFilter}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Dashboard;
